import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import Badge from 'components/Badge';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import { Select } from 'components/FormFields';
import MetricCard from 'components/MetricCard';
import TableSection from 'components/TableSection';
import {
  HeaderContainer,
  IconWrapper,
  HeaderContent,
  Title,
} from 'pages/track/Customers/components/Customers/StyledComponents';
import { useGetAccountReporting } from '../gql/queries/useGetAccountReporting';
import { useGetEventPerformance } from '../gql/queries/useGetEventPerformance';
import { useGetRefundsByAccount } from '../gql/queries/useGetRefundsByAccount';

// Styled Components
const DashboardContainer = styled.div`
  min-height: 100vh;
  background-color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.background.primary
      : props.theme.colors.background.primary};
  padding: 2rem;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: ${(props) =>
    props.noBorder
      ? 'none'
      : props.theme.name === 'dark'
      ? `1px solid ${props.theme.colors.card.border}`
      : `1px solid ${props.theme.colors.card.border}`};
`;

const SummaryItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const SummaryValue = styled.span`
  font-weight: 600;
  color: ${(props) => props.theme.colors.primary};
`;

const SummaryNote = styled.p`
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.text.secondary
      : props.theme.colors.textGray?.secondary || '#64748b'};
  font-size: 0.875rem;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
  margin: 0;
`;

const SummaryText = styled.span`
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.text.primary
      : props.theme.colors.blueBlack};
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
`;

function PromoterDashboardContent() {
  const theme = useTheme();

  // Generate dynamic year options
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const options = ['Year to Date'];

    // Add current year and 4 years back
    for (let i = 0; i < 5; i++) {
      options.push((currentYear - i).toString());
    }

    return options;
  }, []);

  const [selectedPeriod, setSelectedPeriod] = useState('Year to Date');
  const params = useParams();

  // Helper function to get year value for API call
  const getYearFromPeriod = (period) => {
    if (period === 'Year to Date') {
      return new Date().getFullYear();
    }
    return parseInt(period);
  };

  const accountId = parseInt(params.id);
  const year = getYearFromPeriod(selectedPeriod);

  const {
    loading: accountLoading,
    account,
    metrics,
  } = useGetAccountReporting(accountId, year);
  const { data: refundsData, loading: refundsLoading } = useGetRefundsByAccount(
    { accountId: accountId }
  );
  const { data: eventPerformanceData, loading: eventPerformanceLoading } =
    useGetEventPerformance(accountId, year);

  const promoterData = {
    name: account ? account.business_dba : '',
    totalProfit: metrics ? metrics.total_platform_profit : 0,
    appProfit: metrics ? metrics.app_profit : 0,
    posProfit: metrics ? metrics.pos_profit : 0,
    eventCount: metrics ? metrics.events_count : 0,
    stripeBalance: account ? account.stripe_balance : 0,
    payoutBalance: account ? account.payout_balances?.total : 0,
  };

  const events = eventPerformanceData?.getEventPerformance || [];

  const ticketTypes = [
    {
      type: 'General Admission',
      ticketsSold: 1847,
      appSales: 28450,
      posSales: 17825,
      profit: 2234.85,
      avgProfit: 1.21,
    },
    {
      type: 'VIP/Premium',
      ticketsSold: 287,
      appSales: 8925,
      posSales: 5750,
      profit: 1147.3,
      avgProfit: 4.0,
    },
    {
      type: 'Parking',
      ticketsSold: 542,
      appSales: 3250,
      posSales: 2175,
      profit: 294.45,
      avgProfit: 0.54,
    },
  ];

  const membershipTypes = [
    {
      type: 'Season Pass',
      activeMemberships: 342,
      newThisPeriod: 28,
      renewalsThisPeriod: 314,
      appSales: 15600,
      posSales: 8200,
      profit: 1247.5,
      avgProfit: 3.65,
    },
    {
      type: 'VIP Annual',
      activeMemberships: 89,
      newThisPeriod: 12,
      renewalsThisPeriod: 77,
      appSales: 8900,
      posSales: 3200,
      profit: 632.8,
      avgProfit: 7.11,
    },
    {
      type: 'Pit Crew Membership',
      activeMemberships: 156,
      newThisPeriod: 19,
      renewalsThisPeriod: 137,
      appSales: 4680,
      posSales: 2340,
      profit: 365.45,
      avgProfit: 2.34,
    },
    {
      type: 'Junior Racing Club',
      activeMemberships: 67,
      newThisPeriod: 8,
      renewalsThisPeriod: 59,
      appSales: 1340,
      posSales: 670,
      profit: 105.2,
      avgProfit: 1.57,
    },
  ];

  // Process refunds data from API
  const recentRefunds =
    refundsData?.getRefundsByAccount?.results?.map((refund) => ({
      id: refund.id,
      purchaserOrderNumber: refund.purchase.id,
      customerName: refund.user ? refund.user.formatted_name : '',
      eventName: refund?.purchase?.event?.name,
      refundTotal: refund.total,
      trackAmount: refund.track_amount,
      feeImpact: -Math.abs(refund.fees),
      refundDate: new Date(refund.date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
      reason: 'Customer Request',
      status: 'Completed',
    })) || [];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'Processing':
        return 'warning';
      case 'Failed':
        return 'error';
      default:
        return 'neutral';
    }
  };

  // Table column configurations
  const eventColumns = [
    { key: 'date', header: 'Date', type: 'date' },
    {
      key: 'event_name',
      header: 'Event Name',
      type: 'text',
      bold: true,
    },
    { key: 'app_sales', header: 'App Sales', type: 'currency' },
    { key: 'pos_sales', header: 'POS Sales', type: 'currency' },
    {
      key: 'app_profit',
      header: 'App Profit',
      type: 'currency',
      bold: true,
      color: 'primary',
    },
    {
      key: 'pos_profit',
      header: 'POS Profit',
      type: 'currency',
      bold: true,
      color: 'primary',
    },
    {
      key: 'total_profit',
      header: 'Total Profit',
      type: 'currency',
      bold: true,
      color: 'primary',
    },
    {
      key: 'action',
      header: 'Actions',
      type: 'action',
      getLinkTo: (row) => `/admin/events/menu/${row.event_id}`,
    },
  ];

  const refundColumns = [
    {
      key: 'purchaserOrderNumber',
      header: 'Order #',
      type: 'text',
      bold: true,
    },
    { key: 'customerName', header: 'Customer', type: 'text' },
    { key: 'eventName', header: 'Event', type: 'text' },
    { key: 'refundTotal', header: 'Refund Total', type: 'currency' },
    { key: 'trackAmount', header: 'Track Amount', type: 'currency' },
    {
      key: 'feeImpact',
      header: 'Fee Impact',
      type: 'currency',
      bold: true,
      color: theme.colors.error,
    },
    { key: 'refundDate', header: 'Date', type: 'text' },
    { key: 'reason', header: 'Reason', type: 'text' },
    {
      key: 'status',
      header: 'Status',
      type: 'badge',
      getVariant: (value) => getStatusVariant(value),
    },
    {
      key: 'action',
      header: 'Actions',
      type: 'action',
      getLinkTo: (row) =>
        `/admin/transactions/detail/${row.purchaserOrderNumber}`,
    },
  ];

  const ticketColumns = [
    { key: 'type', header: 'Ticket Type', type: 'text', bold: true },
    { key: 'ticketsSold', header: 'Tickets Sold', type: 'number' },
    { key: 'appSales', header: 'App Sales', type: 'currency' },
    { key: 'posSales', header: 'POS Sales', type: 'currency' },
    {
      key: 'profit',
      header: 'Our Profit',
      type: 'currency',
      bold: true,
      color: 'primary',
    },
    {
      key: 'avgProfit',
      header: 'Avg Profit/Ticket',
      type: 'currency',
      bold: true,
      color: 'primary',
    },
  ];

  const membershipColumns = [
    { key: 'type', header: 'Membership Type', type: 'text', bold: true },
    { key: 'activeMemberships', header: 'Active Members', type: 'number' },
    { key: 'newThisPeriod', header: 'New This Period', type: 'text' },
    { key: 'renewalsThisPeriod', header: 'Renewals', type: 'text' },
    { key: 'appSales', header: 'App Sales', type: 'currency' },
    { key: 'posSales', header: 'POS Sales', type: 'currency' },
    {
      key: 'profit',
      header: 'Our Profit',
      type: 'currency',
      bold: true,
      color: 'primary',
    },
    {
      key: 'avgProfit',
      header: 'Avg Profit/Member',
      type: 'currency',
      bold: true,
      color: 'primary',
    },
  ];

  return (
    <DashboardContainer theme={theme}>
      {/* Header */}
      <Card style={{ marginBottom: '30px' }}>
        <HeaderContainer>
          <IconWrapper>
            <svg
              style={{
                width: '32px',
                height: '32px',
                color: theme?.colors?.primary,
              }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </IconWrapper>
          <HeaderContent>
            <Title>Promoter Profitability Dashboard</Title>
          </HeaderContent>
        </HeaderContainer>
      </Card>

      {/* Account Info */}
      <Card
        titleSize="large"
        title={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              flexWrap: 'wrap',
              gap: '1rem',
            }}
          >
            <span>{promoterData.name}</span>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                minWidth: '280px',
              }}
            >
              <label
                htmlFor="timePeriod"
                style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily:
                    'Arial, -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Ubuntu, sans-serif',
                  color:
                    theme?.name === 'dark'
                      ? theme.colors.text.primary
                      : theme.colors.textGray.dark,
                  whiteSpace: 'nowrap',
                }}
              >
                Time Period:
              </label>
              <div style={{ minWidth: '200px' }}>
                <Select
                  id="timePeriod"
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  options={yearOptions.map((option) => ({
                    value: option,
                    label: option,
                  }))}
                />
              </div>
            </div>
          </div>
        }
        style={{ marginBottom: '2rem' }}
      >
        {/* Metrics Grid */}
        <MetricsGrid>
          <MetricCard
            loading={accountLoading}
            value={formatCurrency(promoterData.totalProfit)}
            label="Total Platform Profit"
            skeletonWidth="120px"
          />

          <MetricCard
            loading={accountLoading}
            value={formatCurrency(promoterData.appProfit)}
            label="App Profit"
            skeletonWidth="100px"
          />

          <MetricCard
            loading={accountLoading}
            value={formatCurrency(promoterData.posProfit)}
            label="POS Profit"
            skeletonWidth="100px"
          />

          <MetricCard
            loading={accountLoading}
            value={promoterData.eventCount}
            label="Events This Period"
            skeletonWidth="40px"
          />

          <MetricCard
            loading={accountLoading}
            value={promoterData.stripeBalance}
            label="Stripe Balance"
            skeletonWidth="140px"
          />

          <MetricCard
            loading={accountLoading}
            value={promoterData.payoutBalance}
            label="Payout Balance"
            skeletonWidth="120px"
          />
        </MetricsGrid>
      </Card>

      {/* Event Performance Table */}
      <TableSection
        title="Event Performance"
        action={
          <div style={{ display: 'inline-block' }}>
            <ButtonV2 onClick={() => console.warn('Clicking Click')}>
              Export to CSV
            </ButtonV2>
          </div>
        }
        columns={eventColumns}
        data={events}
        loading={eventPerformanceLoading}
        emptyMessage="No events found for this period."
        testDelay={500}
        minSkeletonDuration={1200}
        refreshKey={selectedPeriod} // Trigger skeleton when year changes
      />

      {/* Recent Refunds Section */}
      <TableSection
        title="Recent Refunds"
        action={
          <Link
            to={`/admin/accounts/refunds/${params.id}`}
            style={{ textDecoration: 'none' }}
          >
            <ButtonV2>View All Refunds</ButtonV2>
          </Link>
        }
        columns={refundColumns}
        data={recentRefunds}
        loading={refundsLoading}
        emptyMessage="No refunds found for this account."
        testDelay={800}
        minSkeletonDuration={1500}
        refreshKey={selectedPeriod} // Trigger skeleton when year changes
      />

      {/* Ticket Type Breakdown */}
      <TableSection
        title="Ticket Type Breakdown"
        columns={ticketColumns}
        data={ticketTypes}
        testDelay={300}
        minSkeletonDuration={1000}
        refreshKey={selectedPeriod} // Trigger skeleton when year changes
      />

      {/* Membership & Annual Sales */}
      <TableSection
        title="Membership & Annual Sales"
        columns={membershipColumns}
        data={membershipTypes}
        testDelay={600}
        minSkeletonDuration={1300}
        refreshKey={selectedPeriod} // Trigger skeleton when year changes
      />

      {/* Summary Cards */}
      <SummaryGrid>
        <Card title="App vs POS Performance">
          <div style={{ marginBottom: '1rem' }}>
            <SummaryItem theme={theme}>
              <SummaryItemLeft>
                <Badge variant="info" size="small" uppercase>
                  APP
                </Badge>
                <SummaryText theme={theme}>66.4% of total profit</SummaryText>
              </SummaryItemLeft>
              <SummaryValue theme={theme}>
                {formatCurrency(promoterData.appProfit)}
              </SummaryValue>
            </SummaryItem>
            <SummaryItem theme={theme} noBorder>
              <SummaryItemLeft>
                <Badge variant="neutral" size="small" uppercase>
                  POS
                </Badge>
                <SummaryText theme={theme}>33.6% of total profit</SummaryText>
              </SummaryItemLeft>
              <SummaryValue theme={theme}>
                {formatCurrency(promoterData.posProfit)}
              </SummaryValue>
            </SummaryItem>
          </div>
          <SummaryNote theme={theme}>
            App generates 2x more profit per dollar sold
          </SummaryNote>
        </Card>

        <Card title="Revenue Breakdown">
          <div style={{ marginBottom: '1rem' }}>
            <SummaryItem theme={theme}>
              <SummaryItemLeft>
                <Badge variant="neutral" size="small" uppercase>
                  EVENTS
                </Badge>
                <SummaryText theme={theme}>Single event tickets</SummaryText>
              </SummaryItemLeft>
              <SummaryValue theme={theme}>
                {formatCurrency(
                  ticketTypes.reduce((sum, ticket) => sum + ticket.profit, 0)
                )}
              </SummaryValue>
            </SummaryItem>
            <SummaryItem theme={theme} noBorder>
              <SummaryItemLeft>
                <Badge variant="info" size="small" uppercase>
                  MEMBERSHIPS
                </Badge>
                <SummaryText theme={theme}>Annual & season passes</SummaryText>
              </SummaryItemLeft>
              <SummaryValue theme={theme}>
                {formatCurrency(
                  membershipTypes.reduce(
                    (sum, membership) => sum + membership.profit,
                    0
                  )
                )}
              </SummaryValue>
            </SummaryItem>
          </div>
          <SummaryNote theme={theme}>
            Memberships provide recurring revenue stream
          </SummaryNote>
        </Card>
      </SummaryGrid>
    </DashboardContainer>
  );
}

export default function PromoterDashboard() {
  return (
    <EnhancedThemeProvider>
      <PromoterDashboardContent />
    </EnhancedThemeProvider>
  );
}
